import React, { useId, useState } from 'react';
import { Box, Chip, Popover, Typography } from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Polling interval for screens that show live route progress.
export const ROUTE_PROGRESS_REFRESH_MS = 2 * 60 * 1000;

// TKT-420: live ahead/behind and predicted finish for one truck, from
// GET /api/routes/live-progress. variance_minutes > 0 means behind plan.
// Click opens how the numbers were worked out.
const RouteProgressChip = ({ progress, sx }) => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const popoverId = useId();

  if (!progress || !progress.stops_total) return null;

  // TKT-427: a finish past midnight printed as "9:30 AM" with no day, so a
  // 27-hour plan looked like an early finish. Add the weekday whenever the
  // time falls on a different day from the route.
  const torontoDay = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const routeDay = progress?.date
    || (progress?.planned_start ? torontoDay(new Date(progress.planned_start)) : torontoDay(new Date()));
  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const sameDay = torontoDay(d) === routeDay;
    return d.toLocaleString(i18n.language, {
      ...(sameDay ? {} : { weekday: 'short' }),
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Toronto',
    });
  };
  const formatDuration = (mins) => {
    const m = Math.abs(Math.round(Number(mins) || 0));
    const hours = Math.floor(m / 60);
    const minutes = m % 60;
    if (hours === 0) return t('routeProgress.minutes', { count: minutes });
    if (minutes === 0) return t('routeProgress.hours', { count: hours });
    return t('routeProgress.hoursMinutes', { hours, minutes });
  };

  const variance = progress.variance_minutes;
  const notStarted = progress.status === 'not_started' || variance == null;
  const finish = formatTime(progress.predicted_finish);

  let label;
  let varianceText = null;
  if (notStarted) {
    label = t('routeProgress.notStartedChip', { time: formatDuration(progress.planned_total_minutes) });
  } else {
    if (variance < 0) varianceText = t('routeProgress.ahead', { time: formatDuration(variance) });
    else if (variance > 0) varianceText = t('routeProgress.behind', { time: formatDuration(variance) });
    else varianceText = t('routeProgress.onTime');
    label = progress.status === 'finished'
      ? t('routeProgress.finishedChip', { variance: varianceText, time: finish })
      : t('routeProgress.liveChip', { variance: varianceText, time: finish });
  }

  let color = 'default';
  if (!notStarted) {
    if (variance <= 0) color = 'success';
    else if (variance <= 15) color = 'warning';
    else color = 'error';
  }

  const open = Boolean(anchorEl);
  const current = progress.current_stop_remaining_minutes;

  return (
    <>
      <Chip
        size="small"
        icon={<ScheduleIcon />}
        color={color}
        variant={notStarted ? 'outlined' : 'filled'}
        label={label}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? popoverId : undefined}
        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, ...sx }}
        data-demo-target="route-progress-chip"
      />
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 340 } } }}
      >
        <Typography variant="subtitle2" component="h4" sx={{ mb: 0.5 }}>
          {t('routeProgress.title')}
        </Typography>
        <Box sx={{ '& > *': { mb: 0.5 } }}>
          {!notStarted && (
            <>
              <Typography variant="body2">
                {t('routeProgress.shouldFinish', { time: formatTime(progress.planned_finish) })}
              </Typography>
              <Typography variant="body2">
                {t('routeProgress.predictedFinish', { time: finish })}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {varianceText}
              </Typography>
            </>
          )}
          <Typography variant="body2">
            {t('routeProgress.stopsDone', { done: progress.stops_done, total: progress.stops_total })}
          </Typography>
          {current != null && (
            <Typography variant="body2">
              {current >= 0
                ? t('routeProgress.currentLeft', { time: formatDuration(current) })
                : t('routeProgress.currentOver', { time: formatDuration(current) })}
            </Typography>
          )}
          <Typography variant="body2">
            {t('routeProgress.plannedBreakdown', {
              work: formatDuration(progress.planned_work_minutes),
              drive: formatDuration(progress.planned_drive_minutes),
              lunch: formatDuration(progress.lunch_minutes),
            })}
          </Typography>
          {!notStarted && (
            <Typography variant="body2">
              {t('routeProgress.lunchUsed', {
                used: progress.lunch_used_minutes,
                total: progress.lunch_minutes,
              })}
            </Typography>
          )}
          {!notStarted && progress.planned_start && (
            <Typography variant="caption" color="text.secondary" component="p">
              {t('routeProgress.leftShop', { time: formatTime(progress.planned_start) })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" component="p">
            {progress.drive_source === 'mapbox'
              ? t('routeProgress.driveMapbox')
              : t('routeProgress.driveDefault')}
          </Typography>
          {progress.gps_fallback_count > 0 && (
            <Typography variant="caption" color="text.secondary" component="p">
              {t('routeProgress.gpsFallback', { count: progress.gps_fallback_count })}
            </Typography>
          )}
          {progress.missing_allotted_count > 0 && (
            <Typography variant="caption" color="warning.main" component="p">
              {t('routeProgress.missingAllotted', { count: progress.missing_allotted_count })}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default RouteProgressChip;
